<template>
  <v-card flat class="pa-6">
    <v-card-title class="text-h6 primary--text mb-6">
      {{ $t("gameplay_settings") }}
    </v-card-title>

    <v-card-text v-if="gameConfig">
      <!-- تنظیمات بازی اول: configConfig -->
      <div
        v-if="gameType === 'Game' && Object.keys(configSchema).length"
        class="mb-8"
      >
        <h4 class="mb-4 primary--text">{{ $t("parameterized_gameplay") }}</h4>
        <v-row dense>
          <v-col
            v-for="(field, key) in configSchema"
            :key="key"
            cols="12"
            md="6"
          >
            <v-text-field
              :value="getSafeValue(`config.${key}`)"
              @input="updatePath(`config.${key}`, Number($event))"
              :label="field.label"
              type="number"
              :min="getMin(key)"
              :max="getMax(key)"
              :step="getStep(key)"
              dense
              outlined
              :hint="getHint(key)"
              persistent-hint
            />
          </v-col>
        </v-row>
      </div>

      <!-- تنظیمات بازی دوم: gameplayConfig -->
      <div
        v-if="
          gameType === 'MeepMeepProRunner' && Object.keys(gameplaySchema).length
        "
        class="mb-8"
      >
        <h4 class="mb-4 primary--text">{{ $t("meepmeep_gameplay") }}</h4>
        <v-row dense>
          <v-col
            v-for="(field, key) in gameplaySchema"
            :key="key"
            cols="12"
            md="6"
          >
            <v-text-field
              :value="getSafeValue(`gameplay.${key}`)"
              @input="updatePath(`gameplay.${key}`, Number($event))"
              :label="field.label"
              type="number"
              :min="getMin(key)"
              :max="getMax(key)"
              :step="getStep(key)"
              dense
              outlined
              :hint="getHint(key)"
              persistent-hint
            />
          </v-col>
        </v-row>
      </div>

      <!-- اگر هیچ تنظیماتی نبود -->
      <v-alert v-if="!hasAnyGameplay" type="info" text prominent>
        تنظیمات گیم‌پلی برای این نوع بازی تعریف نشده است.
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import { get, set } from "lodash";

export default {
  name: "TabGamePlay",

  props: {
    value: { type: Object, required: true },
    schema: { type: Object, required: true },
  },

  computed: {
    gameConfig: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    gameType() {
      return this.gameConfig?.type || null;
    },

    configSchema() {
      return this.schema?.configConfig || {};
    },
    gameplaySchema() {
      return this.schema?.gameplayConfig || {};
    },

    hasAnyGameplay() {
      return (
        Object.keys(this.configSchema).length > 0 ||
        Object.keys(this.gameplaySchema).length > 0
      );
    },
  },

  methods: {
    getSafeValue(path) {
      return get(this.gameConfig, path);
    },
    updatePath(path, val) {
      // Use Vue.set to ensure reactivity
      const parts = path.split('.');
      let obj = this.gameConfig;
      
      // Navigate to the parent object
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) {
          this.$set(obj, parts[i], {});
        }
        obj = obj[parts[i]];
      }
      
      // Set the final property
      this.$set(obj, parts[parts.length - 1], val);
      this.$emit("input", { ...this.gameConfig });
    },

    getMin(key) {
      const mins = {
        initialLives: 1,
        pointsPerQR: 1,
        magnetThreshold: 10,
        magnetDuration: 500,
        spawnRateEnemy: 20,
        spawnRateQR: 30,
        minSpeed: 1,
        maxSpeed: 5,
        lanesCount: 2,
        baseSpeed: 1,
        maxSpeed: 5,
        speedStep: 0.1,
        jumpForce: -30,
        gravity: 0.1,
        minScoreToSubmit: 0,
      };
      return mins[key] || 0;
    },

    getMax(key) {
      const maxs = {
        initialLives: 10,
        pointsPerQR: 100,
        magnetThreshold: 1000,
        magnetDuration: 10000,
        spawnRateEnemy: 300,
        spawnRateQR: 600,
        minSpeed: 20,
        maxSpeed: 50,
        lanesCount: 5,
        baseSpeed: 20,
        maxSpeed: 50,
        speedStep: 5,
        jumpForce: -5,
        gravity: 5,
        minScoreToSubmit: 1000,
      };
      return maxs[key] || 1000;
    },

    getStep(key) {
      const steps = {
        baseSpeed: 0.1,
        maxSpeed: 0.1,
        speedStep: 0.05,
        gravity: 0.05,
        jumpForce: 1,
      };
      return steps[key] || 1;
    },

    getHint(key) {
      const hints = {
        initialLives: "تعداد جان اولیه (۱ تا ۵ توصیه می‌شود)",
        pointsPerQR: "امتیاز هر QR اسکن‌شده",
        magnetThreshold: "امتیاز لازم برای فعال شدن آهنربا",
        magnetDuration: "مدت زمان آهنربا (میلی‌ثانیه)",
        spawnRateEnemy: "فاصله تولید موانع (فریم، کمتر = سخت‌تر)",
        spawnRateQR: "فاصله تولید QRها (فریم)",
        minSpeed: "حداقل سرعت اشیاء",
        maxSpeed: "حداکثر سرعت اشیاء",
        lanesCount: "تعداد لاین‌ها (معمولاً ۳)",
        baseSpeed: "سرعت شروع بازی",
        maxSpeed: "حداکثر سرعت",
        speedStep: "افزایش سرعت هر مرحله",
        jumpForce: "قدرت پرش (عدد منفی)",
        gravity: "نیروی جاذبه",
        minScoreToSubmit: "حداقل امتیاز برای نمایش فرم شماره",
      };
      return hints[key] || "";
    },
  },
};
</script>

<style scoped>
h4 {
  border-right: 4px solid var(--v-primary-base);
  padding-right: 8px;
  font-size: 1rem;
}
</style>
